import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useContext, useMemo, useState } from 'react';
import { PopularBrands } from 'src/components/shared/brands/PopularBrands';
import { CategoryComponentWithSlug } from 'src/components/shared/category/CategoryComponentWithSlug';
import { CategoryList } from 'src/components/shared/category/CategoryList';
import { CategoryRanking } from 'src/components/shared/category/CategoryRanking';
import { CategoryTitle } from 'src/components/shared/category/CategoryTitle';
import { VendorName } from 'src/components/shared/category/VendorName';
import { BannerSlider } from 'src/components/shared/elements/banners/BannerSlider';
import { Loading } from 'src/components/shared/elements/loading';
import { Navigator } from 'src/components/shared/elements/navigator';
import { MainContainer, MainContainerWithSideBar } from 'src/components/shared/layouts/MainContainer';
import { HtmlHead } from 'src/components/shared/meta/HtmlHead';
import { FeaturedProducts } from 'src/components/shared/sections/FeaturedProducts';
import { SideMenuBar } from 'src/components/shared/templates/SideMenuBar';
import { KaimonoWebRoutes } from 'src/config/routes';
import { CATEGORY_ID } from 'src/constants/category';
import { NEW_ARRIVALS_FEATURE_TAG, PICKUP_FEATURE_TAG, QUICK_SHIP_FEATURE_TAG } from 'src/constants/featureTags';
import { useCategory } from 'src/hooks/useCategory';
import { useCategoryPageMetaData } from 'src/hooks/useMetaData';
import { fetchBanners } from 'src/libs/api/bannerApi';
import { fetchLargeCategories } from 'src/libs/api/categoryApi';
import type { BannerSchema } from 'src/models/banner/schema';
import type { LargeCategorySchema } from 'src/models/category/schema';
import { BrandsContext } from 'src/stores/Brands';
import { I18nContext } from 'src/stores/I18n';
import type { LOCALE_TYPE } from 'src/types/locale';
import styles from './index.module.scss';

const ALL = 'all';
const DRUG = 'drug';
const ALCOHOL = 'alcohol';
const NEW_ARRIVALS_SECTION_TITLE = 'NEW ARRIVALS';
const COSMETICS = 'cosmetics';

type Props = {
  banners: BannerSchema[];
  largeCategory: LargeCategorySchema;
  middleCategoryIds: number[];
  largeCategorySlug: string;
};

const LargeCategoryPage: NextPage<Props> = ({ banners, largeCategory, middleCategoryIds, largeCategorySlug }) => {
  const router = useRouter();
  const { category, isLoading, isError } = useCategory();
  const middleCategories = useMemo(() => {
    if (!category) return [];
    return category.middleCategories.filter((middleCategory) =>
      middleCategoryIds.includes(middleCategory.middleCategoryId)
    );
  }, [category, middleCategoryIds]);
  const middleCategorySlugs = middleCategories.map((middleCategory) => middleCategory.slug);
  const brands = useContext(BrandsContext);
  const [activeCategorySlug, setActiveCategorySlug] = useState<string>(ALL);
  const isCategoryDrug = largeCategorySlug === DRUG;
  const isCategoryAlcohol = largeCategorySlug === ALCOHOL;
  const isCategoryCosmetics = largeCategorySlug === COSMETICS;
  const localeResources = useContext(I18nContext);

  const { title: metaTitle, description: metaDescription } = useCategoryPageMetaData(
    router.query,
    brands,
    largeCategory?.name ?? '',
    localeResources
  );

  if (isLoading) return <Loading />;
  if (isError) router.push(KaimonoWebRoutes.index);

  return (
    <div>
      <HtmlHead
        title={metaTitle}
        description={metaDescription}
        image={largeCategory.logoUrl}
        robotsIndex
        path={KaimonoWebRoutes.largeCategories.show(largeCategory.slug)}
      />
      <Navigator
        items={[
          {
            unprefixedPath: KaimonoWebRoutes.largeCategories.show(largeCategory.slug),
            label: largeCategory.name
          }
        ]}
      />
      {banners?.length > 0 && <BannerSlider banners={banners} />}
      <MainContainer isFlex>
        <SideMenuBar />
        <MainContainerWithSideBar>
          <CategoryTitle
            text={isCategoryDrug ? largeCategory.name : localeResources.commons.bestJapanese + largeCategory.name}
          />
          <div className={styles.vendorNameContainer}>
            {isCategoryDrug && <VendorName categorySlug={largeCategorySlug} />}
            {isCategoryAlcohol && <VendorName categorySlug={largeCategorySlug} />}
          </div>
          {middleCategories && (
            <div className={styles.categoryListContainer}>
              <CategoryList
                categories={middleCategories}
                categoryType="middleCategory"
                activeCategorySlug={activeCategorySlug}
                setActiveCategorySlug={setActiveCategorySlug}
              />
            </div>
          )}
          <div className={styles.pickupContainer}>
            <FeaturedProducts
              featureTag={PICKUP_FEATURE_TAG}
              productCountPerPage={16}
              title={localeResources.featured.pickUp.title}
              additionalParam={{
                type: CATEGORY_ID.LARGE,
                id: largeCategory.largeCategoryId
              }}
            />
          </div>
          <CategoryRanking categoryName={largeCategory.name} categorySlug={largeCategory.slug} />
          {largeCategory && (
            <div className={styles.featuredProductsContainer}>
              <FeaturedProducts
                featureTag={NEW_ARRIVALS_FEATURE_TAG}
                productCountPerPage={16}
                title={NEW_ARRIVALS_SECTION_TITLE}
                additionalParam={{
                  type: CATEGORY_ID.LARGE,
                  id: largeCategory.largeCategoryId
                }}
              />
            </div>
          )}
          {isCategoryCosmetics && <PopularBrands isReadMore={false} />}
          {middleCategories &&
            middleCategorySlugs &&
            middleCategorySlugs.map((slug) => (
              <div key={slug}>
                <CategoryComponentWithSlug slug={slug} categories={middleCategories} categoryType="middleCategory" />
              </div>
            ))}
          <div className={styles.featuredProductsContainer}>
            <FeaturedProducts
              featureTag={QUICK_SHIP_FEATURE_TAG}
              productCountPerPage={16}
              title={localeResources.featured.quickShip.title}
              additionalParam={{
                type: CATEGORY_ID.LARGE,
                id: largeCategory.largeCategoryId
              }}
            />
          </div>
        </MainContainerWithSideBar>
      </MainContainer>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const slug = params?.slug;
  const largeCategories = await fetchLargeCategories(locale as LOCALE_TYPE);
  const largeCategory = largeCategories.find((largeCategory) => largeCategory.slug === slug);
  const middleCategoryIds = largeCategory?.middleCategoryIds;
  const largeCategorySlug = largeCategory?.slug;
  const banners = await fetchBanners(largeCategorySlug ?? '', locale as LOCALE_TYPE);
  if (!slug) return { notFound: true };
  if (!largeCategory) return { notFound: true };
  return {
    props: { banners, largeCategory, middleCategoryIds, largeCategorySlug }
  };
};

export default LargeCategoryPage;
